import cn from "@/v2-ui/utils/utils.cn"
import { SUMMARY_STACK_MAX_SEGMENTS } from "./table.summary.constants"
import type {
  SummaryBreakdownEntry,
  SummaryVisual,
} from "./table.summary.types"
import { getBreakdownEntryColor } from "./table.summary.utils"

export type TableSummaryVisualProps = {
  visual: SummaryVisual
  className?: string
}

function toPercent(share: number): string {
  return `${Math.min(Math.max(share, 0), 1) * 100}%`
}

/**
 * The segments of a stacked bar: one per entry up to the palette's reach,
 * then a single segment for everything else.
 */
function stackSegments(
  entries: SummaryBreakdownEntry[],
): { key: string; share: number; color: string }[] {
  const named = entries.slice(0, SUMMARY_STACK_MAX_SEGMENTS)
  const rest = entries.slice(SUMMARY_STACK_MAX_SEGMENTS)
  const segments = named.map((entry, index) => ({
    key: entry.key,
    share: entry.share,
    color: getBreakdownEntryColor(index),
  }))
  if (rest.length > 0) {
    segments.push({
      key: "__other__",
      share: rest.reduce((sum, entry) => sum + entry.share, 0),
      color: getBreakdownEntryColor(SUMMARY_STACK_MAX_SEGMENTS),
    })
  }
  return segments
}

/**
 * A thin bar that pictures a summary the way the answer alone can't: a
 * breakdown's composition (stacked, coloured per entry), a count's or share's
 * proportion of the rows (a meter), or where an average or median sits
 * between the column's min and max (a marker on a track). Decorative — the
 * value beside it carries the meaning — so hidden from assistive tech.
 */
function TableSummaryVisual(props: TableSummaryVisualProps) {
  const { visual, className } = props

  if (visual.type === "stack") {
    return (
      <span
        className={cn(
          "ui-table-summary-visual ui-table-summary-stack",
          className,
        )}
        aria-hidden
      >
        {stackSegments(visual.entries).map((segment) => (
          <span
            key={segment.key}
            className="ui-table-summary-stack-segment"
            style={{
              width: toPercent(segment.share),
              backgroundColor: segment.color,
            }}
          />
        ))}
      </span>
    )
  }

  if (visual.type === "meter") {
    return (
      <span
        className={cn(
          "ui-table-summary-visual ui-table-summary-meter",
          className,
        )}
        aria-hidden
      >
        <span
          className="ui-table-summary-meter-fill"
          style={{ width: toPercent(visual.share) }}
        />
      </span>
    )
  }

  return (
    <span
      className={cn(
        "ui-table-summary-visual ui-table-summary-range",
        className,
      )}
      aria-hidden
    >
      <span
        className="ui-table-summary-range-marker"
        style={{ left: toPercent(visual.share) }}
      />
    </span>
  )
}

export default TableSummaryVisual
